import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clampModalSize,
  defaultModalSize,
  modalSizeKey,
  parseModalSize,
  serialiseModalSize,
  type ModalSize,
  type Viewport,
} from '@/utils/modal-size';

const readViewport = (): Viewport => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

/**
 * One modal's remembered size.
 *
 * Storage access is wrapped because it throws outright in private-browsing
 * modes and when a quota is exhausted. Losing persistence there is acceptable;
 * taking the modal down with it is not. Same bargain as useAssemblyDraft.
 */
export function useModalSize(storageKey: string, isOpen: boolean, enabled: boolean) {
  const [viewport, setViewport] = useState<Viewport>(readViewport);
  const [size, setSize] = useState<ModalSize | null>(null);

  // Mirrors `size` for commit(), which runs from a pointerup handler and must
  // read the value the last pointermove produced, not the one captured when
  // the handler was created.
  const latest = useRef<ModalSize | null>(null);

  // Sized when the modal OPENS, not at mount: the viewport may have changed
  // since, and a size stored on a wider monitor has to be clamped against
  // whichever screen is in use now.
  useEffect(() => {
    if (!isOpen || !enabled) {
      setSize(null);
      latest.current = null;
      return;
    }
    const vp = readViewport();
    let stored: ModalSize | null = null;
    try {
      stored = parseModalSize(window.localStorage.getItem(modalSizeKey(storageKey)));
    } catch {
      /* storage unavailable — open at the default */
    }
    const next = clampModalSize(stored ?? defaultModalSize(vp), vp);
    setViewport(vp);
    setSize(next);
    latest.current = next;
  }, [isOpen, enabled, storageKey]);

  // Shrinking the browser window while the modal is open must not strand the
  // footer off-screen.
  useEffect(() => {
    if (!isOpen || !enabled) return;
    const onResize = () => {
      const vp = readViewport();
      setViewport(vp);
      setSize((current) => {
        if (!current) return current;
        const next = clampModalSize(current, vp);
        latest.current = next;
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, enabled]);

  const resize = useCallback((next: ModalSize) => {
    const clamped = clampModalSize(next, readViewport());
    latest.current = clamped;
    setSize(clamped);
  }, []);

  // Called once per drag gesture, on pointerup — not on every pointermove.
  const commit = useCallback(() => {
    if (!latest.current) return;
    try {
      window.localStorage.setItem(modalSizeKey(storageKey), serialiseModalSize(latest.current));
    } catch {
      /* storage unavailable — the session still works, it just won't persist */
    }
  }, [storageKey]);

  return { size, viewport, resize, commit };
}
